// difficultyCalculator.ts - Funktionen zur Berechnung der Schwierigkeit von Trampolin-Bewegungen

import { normalizeInput } from './normalizeUtils';
import { DifficultyData } from './types';

/**
 * Sprungnotation (FIG-Standard):
 *
 *   <Zahlenteil> <Position> [<Landung>]
 *
 * Zahlenteil – zweistellig, kodiert Salto- und Schraubenrotation:
 *   Erste Ziffer  = Saltorotation   0=0°  1=90°  2=180°  3=270°  4=360°  5=540°  ...
 *   Zweite Ziffer = Schrauben       0=keine  1=½  2=1  3=1½  4=2  ...
 *   Sonderfall 42–49: kombinierte Elemente (z. B. Barani-Ball-out), keine Positionsangabe nötig.
 *
 * Positionszeichen (nach Normalisierung):
 *   /  = gebückt (Pike)
 *   <  = gehockt (Tuck)
 *   °  = gestreckt (Straight)
 *   V  = Sitzsprung (nur bei "00V" erlaubt)
 *
 * Landungsangabe (optional, am Ende):
 *   B  = Bauchlandung
 *   R  = Rückenlandung
 *   S  = Sitzlandung
 *
 * Beispiele:
 *   "30/B"  – ¾-Salto vorwärts gebückt zur Bauchlandung
 *   "41°"   – Salto vorwärts gestreckt mit ½ Schraube (Barani c)
 *   "51°"   – 1½-Salto gestreckt (Cody c)
 *   "00V"   – Sitzsprung
 */

/**
 * Ermittelt den Schwierigkeitswert (SW) für einen einzelnen Sprung.
 *
 * Ablauf:
 *  1. Eingabe normalisieren (z. B. 'o'→'°', '\'→'/')
 *  2. Zahlenteil und Positionsteil trennen und gegen erlaubte Muster prüfen
 *  3. Bei Sondernummern 42–49 ohne Positionszeichen: direkter Lookup,
 *     Fallback auf Variante mit '/'
 *  4. Vollständiges Symbol in der SW-Tabelle (difficultyData.jumpDifficulties) nachschlagen
 *
 * Gibt 0 zurück bei leerem Symbol, ungültigem Format oder fehlendem Eintrag in der Tabelle.
 */
export const getDifficultyForSymbol = (
  symbol: string,
  difficultyData: DifficultyData
): number => {
  if (!symbol) return 0;

  const normalizedInput = normalizeInput(symbol);

  if (normalizedInput.length < 2) {
    return 0;
  }

  const numericPart = normalizedInput.match(/^[0-9]+/)?.[0] || "";
  const rest = normalizedInput.substring(numericPart.length);

  const validNumericPattern = /^(00|0[1-9]|[1-6][0-9]|[7-9][0-9]{2}|10[0-9]{2}|1[1-4][0-9]{3}|1[5-8][0-9]{4})$/;
  if (!validNumericPattern.test(numericPart)) {
    return 0;
  }

  // 42–49: kombinierte Elemente ohne Pflicht-Positionszeichen
  const isSpecialNumber = /^4[2-9]$/.test(numericPart);

  const validPositionLandingPattern = /^[/<°V][BRS]?$/;

  if (!isSpecialNumber && !validPositionLandingPattern.test(rest)) {
    return 0;
  }

  const symbolToDifficulty = difficultyData.jumpDifficulties;

  if (isSpecialNumber && rest === "") {
    if (normalizedInput in symbolToDifficulty) {
      return symbolToDifficulty[normalizedInput];
    }
    const withDefaultPosition = normalizedInput + "/";
    if (withDefaultPosition in symbolToDifficulty) {
      return symbolToDifficulty[withDefaultPosition];
    }
    return 0;
  }

  return normalizedInput in symbolToDifficulty
    ? symbolToDifficulty[normalizedInput]
    : 0;
};

/**
 * Berechnet die SW-Werte für alle Sprünge aller Übungen.
 *
 * Gibt eine Matrix zurück, die strukturell identisch mit dem exercises-Array ist:
 * difficultyScores[i][j] ist der SW-Wert von exercises[i][j].
 * Unbekannte oder leere Sprünge erhalten den Wert 0.
 */
export const calculateDifficultyScores = (
  exercises: string[][],
  difficultyData: DifficultyData
): number[][] => {
  return exercises.map((exercise) =>
    exercise.map((move) => getDifficultyForSymbol(move, difficultyData))
  );
};

/**
 * Summiert die SW-Werte je Übung.
 *
 * Gibt ein Array zurück, in dem totalDifficulty[i] die Gesamtschwierigkeit
 * der i-ten Übung enthält.
 */
export const calculateTotalDifficulty = (
  difficultyScores: number[][]
): number[] => {
  return difficultyScores.map(scores =>
    scores.reduce((sum, score) => sum + score, 0)
  );
};
